import { getAPI, getStatus } from 'caccl/client';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { getCanvasSemseterPrefix, getSemesterFromSisCode, sendApiRequest } from '../lib/helper';
import { IAuthenticationStatusContext } from '../types/IAuthenticationStatusContext';
import { ICourse, ISemester, Semester } from '../types/ICourse';
import { IApiContext } from '../types/Shared';

interface IAuthProviderContext extends IApiContext<IAuthenticationStatusContext> {
    ClearCurrentCanvasCourseFromAuthContext: () => void;
}

export const AuthenticationContext = createContext<IAuthProviderContext>({ Pending: false, ClearCurrentCanvasCourseFromAuthContext: () => {} });
export function useAuthenticationContext() {
    return useContext(AuthenticationContext);
}

export function AuthenticationProvider({ children }: PropsWithChildren<{}>) {

    const [authStatus, setAuthStatus] = useState<IAuthenticationStatusContext>();
    const [error, setError] = useState();
    const [pending, setPending] = useState(true);

    useEffect(() => {
        setError(undefined);
        setPending(true);
        getStatus().then((cacclStatus: any) => {
            const authStatus: IAuthenticationStatusContext = {
                IsAuthenticated: cacclStatus.launched,       
                Courses: [],
                IsAdmin: false  
            }
            if(cacclStatus.launched)
            {
                if(cacclStatus.launchInfo.tenantId)
                {
                    authStatus.KeplerAuthStatus = cacclStatus.launchInfo;
                }
                else
                {
                    authStatus.CanvasAuthStatus = cacclStatus.launchInfo;
                }
                if(authStatus.CanvasAuthStatus)
                {
                    getAPI().then(api => {
                        api.other.endpoint({
                            path: "/api/v1/courses", 
                            method: "GET"
                        }).then(async (courses) => {
                            let semesters: any[] = [];   
                            try
                            {                               
                                semesters = await sendApiRequest("/api/canvas/getListOfSemestersFromCanvas", "GET");                                
                            }
                            catch(err)
                            {
                                /*gulp*/
                            } 
                            authStatus.Courses = courses.filter((c: any) => c?.course_code).map((c: any) => {
                                try
                                {
                                    const courseSplit = c.course_code.split(" ").filter((cc: string) => cc.length > 0);
                                    let courseNumber = "";
                                    let courseNum = "UNKNOWN";
                                    let sectionNum = "UNKNOWN";
                                    let department = courseSplit[0] || "UNKNOWN";
                                    if (courseSplit.length > 0) {
                                        courseNumber = courseSplit[1] ? courseSplit[1].trim() : "";
                                    }
                            
                                    if(courseNumber.length >= 3)
                                    {
                                        courseNum = courseNumber.substring(0, 3);
                                    }
                                    if(courseNumber.length > 3)
                                    {
                                        sectionNum = courseNumber.substring(3);
                                    }
                                    if(sectionNum === "UNKNOWN" && courseSplit[courseSplit.length - 1]?.endsWith("MC"))
                                    {
                                        sectionNum = "MC";
                                    }
                                    let sisCode = undefined;
                                    let semester: ISemester;
                                    let year = undefined;
                                    let semesterStr: Semester;
                                    if(c.sis_course_id)
                                    {                                    
                                        semester = getSemesterFromSisCode(c.sis_course_id)!;
                                        const semseterAsSemesterCode = getCanvasSemseterPrefix(semester.Semester);
                                        sisCode = `${semester.Year}${semseterAsSemesterCode}.${department}.${courseNum}.${sectionNum}`;
                                        year = semester.Year;
                                        semesterStr = semester.Semester;
                                    }
                                    else
                                    {
                                        const semester = semesters.find(s => s.TermId === c.enrollment_term_id)
                                        if(!semester)
                                        {
                                            console.log(`Unable to find Canvas course semeseter with term id: ${c.enrollment_term_id}`);
                                        }
                                        year = semester.Year;
                                        semesterStr = semester.Semester;
                                        const semesterCanvasNumber = semester.Prefix;                                   
                                        sisCode = `${year}${semesterCanvasNumber}.${department}.${courseNum}.${sectionNum}`;
                                    }

                                    if(sisCode.endsWith(".UNKNOWN.UNKNOWN")){
                                        sisCode = c.course_code
                                    }
                                    
                                    const blobFolderName = `${department} ${courseNum}-${sectionNum} ${c.name}`;
                                    let course: ICourse = {
                                        Course: c.course_code,
                                        Title: c.name,
                                        SIS: sisCode,
                                        Department: "",
                                        Semester: semesterStr,
                                        Year: year,
                                        Instructor: "",
                                        IsMyCourse: true,
                                        CanvasId: c.id,
                                        BlobFolderName: blobFolderName,
                                        SyllabusBody: ""                    
                                    };
                                    return course;
                                }
                                catch(err)
                                {
                                    console.log(`Error getting course from canvas`, err);
                                }
                            }).filter((c: ICourse) => c);
                            setAuthStatus(authStatus);
                        }).catch(err => {
                            console.log("my courses", err);
                        })
                    })
                    authStatus.UserInfo = {
                        DisplayName: `${cacclStatus.launchInfo.userLastName}, ${cacclStatus.launchInfo.userFirstName}`
                    }
                }
                else if(authStatus.KeplerAuthStatus)
                {
                    let rolesDisplay = [];
                    if(authStatus.KeplerAuthStatus.idTokenClaims.companyName && authStatus.KeplerAuthStatus.idTokenClaims.companyName.length > 0)
                    {
                        rolesDisplay.push(`Accreditor - ${authStatus.KeplerAuthStatus.idTokenClaims.companyName}`)
                    }
                    if(authStatus.KeplerAuthStatus.isKeplerAdministrator)
                    {
                        rolesDisplay.push("Admin");
                    }
                    let rolesDisplayName = "";
                    if(rolesDisplay.length > 0)
                    {
                        rolesDisplayName = `(${rolesDisplay.join(", ")})`
                    }                    
                    authStatus.UserInfo = {
                        DisplayName: `${authStatus.KeplerAuthStatus.account.name} ${rolesDisplayName}`
                    }
                    if(rolesDisplay.length === 0)
                    {
                        authStatus.UserInfo.NoCompanyName = true;
                    }
                }
            }
            setAuthStatus(authStatus);            
        }).catch(err => {           
            setError(err);            
        }).finally(() => {
            setPending(false);
        });
    }, []);

    const clearCurrentCanvasCourseFromAuthContext = () => {
        
        if(authStatus?.CanvasAuthStatus?.sisCourseId)
        {
            const currentAuthStatus = {...authStatus};
            currentAuthStatus.CanvasAuthStatus!.sisCourseId = "";
            setAuthStatus(currentAuthStatus);
        }
    }

    return (
        <AuthenticationContext.Provider value={{ Error: error, Pending: pending, Result: authStatus, ClearCurrentCanvasCourseFromAuthContext: clearCurrentCanvasCourseFromAuthContext }}>
            {children}
        </AuthenticationContext.Provider>
    );
}